/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericButton from '@ui-components/Button/GenericButton'
import { GenericTable } from '@ui-components/Table/GenericTable'
import React, { useEffect, useMemo, useState } from 'react'
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusV3.svg";

import { CustomerTableRow, getCustomerColumns } from './CustomersTableColumns';
import { getTenantById, getTenants, Tenant, TenantDetail } from '../request/request';
import GenericCard from '@ui-components/Card/GenericCard';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import { buildPredicate, FilterField, labelId, parseToken } from 'kontrolEdge/helpers/advancedFilters';
import { useAuth0 } from '@auth0/auth0-react';
import { SkeletonTableRows } from '@ui-components/Skeleton/GenericSkeleton';
import { CustomerEditModal, CustomerViewModal } from './CustomerModal';


export default function CustomerPage() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [customerList, setCustomerList] = useState<CustomerTableRow[]>([]);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<TenantDetail | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);

// ===== 2) FETCH TENANTS ON MOUNT =====
useEffect(() => {
if (isLoading) return;
if (!isAuthenticated) return;

const fetchTenants = async () => {
try {
  setIsFetching(true);
  const token = await getAccessTokenSilently();
  const resp = await getTenants(token);
  const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
  
  // Filter for CUSTOMER type (TenantType.id === 3)
  const customers = rawData.filter((tenant: Tenant) => 
    tenant.TenantType?.id === 3
  );
  
  // Map Tenant to CustomerTableRow
  const mappedCustomers: CustomerTableRow[] = customers.map((tenant: Tenant) => ({
    id: tenant.id,
    displayName: tenant.nameDisplay || tenant.Name,
    name: tenant.Name,
    chargebeeCustomerId: tenant.chargebeeCustomerId,
  }));
  
  setCustomerList(mappedCustomers);
} catch (err) {
  console.error("Error fetching tenants:", err);
} finally {
  setIsFetching(false);
}
};

fetchTenants();
}, [isLoading, isAuthenticated, getAccessTokenSilently]);

// ===== 2.1) FETCH CUSTOMER DETAIL =====
useEffect(() => {
if (!selectedCustomerId || !isAuthenticated) return;

const fetchCustomerDetail = async () => {
try {
  setIsModalLoading(true);
  const token = await getAccessTokenSilently();
  const resp = await getTenantById(token, selectedCustomerId);
  const customerData = (resp as any)?.data?.data ?? (resp as any)?.data;
  setSelectedCustomer(customerData);
} catch (err) {
  console.error("Error fetching customer detail:", err);
} finally {
  setIsModalLoading(false);
}
};

fetchCustomerDetail();
}, [selectedCustomerId, isAuthenticated, getAccessTokenSilently]);

// ===== 3) HANDLERS =====
const handleViewCustomer = (c: CustomerTableRow) => {
setSelectedCustomerId(c.id);
setIsViewModalOpen(true);
setSelectedCustomer(null); // Clear previous data
};

const handleEditCustomer = (c: CustomerTableRow) => {
setSelectedCustomerId(c.id);
setIsEditModalOpen(true);
setSelectedCustomer(null); // Clear previous data
};

const handleDeleteClick = (c: CustomerTableRow) => {
console.log('deleting', c);
};

const handleAddCustomer = () => {
setSelectedCustomerId(null);
setSelectedCustomer(null);
setIsModalLoading(false); // No loading for new customer
setIsEditModalOpen(true);
};

const handleCloseViewModal = () => {
setIsViewModalOpen(false);
setSelectedCustomerId(null);
setSelectedCustomer(null);
};

const handleCloseEditModal = () => {
setIsEditModalOpen(false);
setSelectedCustomerId(null);
setSelectedCustomer(null);
};

const handleSaveCustomer = () => {
// TODO: Implement save logic
console.log('Saving customer:', selectedCustomer);
handleCloseEditModal();
};

const handleFieldChange = (field: string, value: string | boolean) => {
if (!selectedCustomer) return;
setSelectedCustomer({
...selectedCustomer,
[field]: value,
});
};

// ===== 4) FILTERING =====
function makeCustomerFields(): FilterField<CustomerTableRow>[] {
return [
{ label: "ID", key: "id", type: "number", accessor: r => r.id },
{ label: "Customer Name", key: "customername", type: "string", accessor: r => r.displayName, inFreeText: true },
{ label: "Name", key: "name", type: "string", accessor: r => r.name, inFreeText: true },
{ label: "Chargebee Customer ID", key: "chargebeecustomerid", type: "string", accessor: r => r.chargebeeCustomerId || "" },
];
}

const customerFields = useMemo(() => makeCustomerFields(), []);

const fieldById = useMemo(() => {
const m = new Map<string, FilterField<CustomerTableRow>>();
for (const f of customerFields) {
const id = (f.key ?? labelId(f.label)).toLowerCase();
m.set(id, f);
}
return m;
}, [customerFields]);

const keyMap = useMemo(() => {
const m = new Map<string, string>();
for (const f of customerFields) {
const id = (f.key ?? labelId(f.label)).toLowerCase();
const label = f.label.toLowerCase();
m.set(id, id);
m.set(label, id);
}
return m;
}, [customerFields]);

const normalizedFilters = useMemo(() => {
return activeFilters.map((f) => {
const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

if (token.key) {
  const k = token.key.trim().toLowerCase();
  token.key = keyMap.get(k) ?? labelId(token.key);
}

const field = token.key ? fieldById.get(token.key) : undefined;
if (field?.type === "string" && token.value) {
  const quoted = token.value.match(/^"(.*)"$/);
  if (quoted) {
    token.value = quoted[1];
    token.op = "=";
  } else if (!token.op) {
    token.op = ":"; // contains
  }
}
return token;
});
}, [activeFilters, keyMap, fieldById]);

const predicate = useMemo(
() => buildPredicate<CustomerTableRow>(normalizedFilters, customerFields),
[normalizedFilters, customerFields]
);

const filteredCustomers = useMemo(
() => customerList.filter(predicate),
[customerList, predicate]
);

const suggestedKeys = useMemo(() => {
return customerFields.map(f => f.label);
}, [customerFields]);

// Safely pass them into getCustomerColumns:
const columns = getCustomerColumns({
handleViewCustomer,
handleEditCustomer,
handleDeleteClick,
});

return (
<GenericCard isCollapsible={false}>
{/* Header with Searchbar and Add Button */}
<div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
  <div className="flex items-end justify-between">
    <div className="w-[520px] my-5">
      <GenericSearchbar
        placeholder="Find by attribute or value"
        suggestedKeys={suggestedKeys}
        onFiltersChange={setActiveFilters}
      />
    </div>
    {/* <div className="mb-10">
      <GenericButton
        label={
          <div className="flex items-center gap-2">
            <IconPlus className="w-5 h-5" />
            <span>Create new customer</span>
          </div>
        }
        variant="brand"
        size="md"
        onClick={handleAddCustomer}
      />
    </div> */}
  </div>
</div>

{/* Table */}
<div className="px-5 mb-5">
  {isFetching ? (
    <div className="space-y-2">
      <SkeletonTableRows rows={5} cols={4} />
    </div>
  ) : (
    <GenericTable
      columns={columns}
      data={filteredCustomers}
      width="100%"
      initialRowsPerPage={5}
      rowsPerPageOptions={[5, 10]}
      tableClassName="bg-semantic-bg-primary"
    />
  )}
</div>

{/* View Modal */}
<CustomerViewModal
  isOpen={isViewModalOpen}
  isLoading={isModalLoading}
  customer={selectedCustomer}
  onClose={handleCloseViewModal}
/>

{/* Edit Modal */}
<CustomerEditModal
  isOpen={isEditModalOpen}
  isLoading={isModalLoading}
  customer={selectedCustomer}
  onClose={handleCloseEditModal}
  onSave={handleSaveCustomer}
  onFieldChange={handleFieldChange}
/>
</GenericCard>
)
}
